import React from 'react';
import * as Sentry from '@sentry/react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { connect } from 'react-redux';
import { StackConstraint } from './store/types';
import { clearBoostedPlayer, getContestLineups, getContestPlayers, getContestStacks, setBoostedPlayer, setConstraints, setGeneratedSlate, setPoolSize, setRunback, triggerContestGen } from './store/actions';
import { useParams } from 'react-router-dom';
import { APIState } from '../utils/api/reducer';
import { AuthResponse } from '../preContest/store/types';
import { UpgradeModal } from '../common/LockIcon';
import { useGetSlateInfoQuery, useGetSlates } from '../common/queries/slateQueries';
import { Alert, Stack, Box, Grid, Button, Snackbar } from '@mui/material';
import moment from 'moment';
import { APIConstants } from '../utils/api/constants';
import { siteLogos, sportLogos } from '../utils/logos';
import { useUserId } from '../utils/authentication';
import Upload from '../preContest/upload';
import { lateSwapSupportedSport } from './utils';
import UnauthenticatedView from '../common/UnauthenticatedView';
import SettingsSelectors from '../common/SettingsSelectors';
import NoSlatesView from '../common/NoSlatesView';
import { SitesResponse, SlateResponse, SportsResponse } from '../common/store/types';
import { SettingsLoader } from '../common/SettingsLoader';
import { filterSlates } from '../common/helpers/SelectorHelpers';
import { userSubscribed } from '../utils/authentication';

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    backgroundColor: 'rgba(219, 77, 0, 0.20)',
    width: '100%',
    padding: '7px 20px',
    position: 'relative',
    '& .MuiButtonBase-root': {
      border: '1px solid rgba(255, 255, 255, 0.20)',
      backgroundColor: '#0A0A0A',
    },
    '& .hide': {
      display: 'none',
    },
    '& .chooseFile': { height: '35px' }
  },
  formWrapper: {
    color: 'white',
    marginTop: 20,
    '& .MuiInputBase-root': {
      color: 'white',
      backgroundColor: '#000A16',
      borderRadius: 3,
      padding: 8,
      border: `1px solid rgba(238, 238, 238, .2)`,
      marginBottom: 4,
    },
    '& .MuiFormLabel-root': {
      color: 'white',
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
    '& button': {
      backgroundColor: '#0042C1 !important',
      padding: 8,
    },
    '& .Mui-disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    },
    '& .MuiSelect-select.MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  clear: {
    // backgroundColor: 'transparent',
  },
  restart: {
    borderRadius: 3,
    border: '1px solid rgba(255, 255, 255, 0.20)',
    color: 'white',
    padding: '8px 16px',
    marginLeft: 8,
    textTransform: 'none',
    '&:disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    }
  },
  advancedButton: {
    background: '#0042C1',
    width: '100%',
    color: 'white',
    fontFamily: "Inter",
  },
  signInButtonWrapper: {
    padding: 0,
  },
  signInButton: {
    width: '100%',
    border: '1px solid #0042C1',
    color: 'white',
    fontFamily: "Inter",
  },
  sliderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    marginLeft: 8,
    '& .MuiSlider-markLabel': {
      color: 'white',
    },
    // '& .MuiSlider-track': {
    //   backgroundColor: 'transparent',
    // }
  },
  hideButton: {
    cursor: 'pointer',
    marginLeft: 12,
    textDecorationLine: 'underline',
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 500,
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    fontFamily: 'Inter',
  },
  selectWrapper: {
    textOverflow: 'wrap',
    '& .MuiFormLabel-root': {
      color: theme.palette.common.white,
      opacity: 0.7,
      padding: 4,
      fontFamily: 'Inter',
      fontSize: '12px',
      fontStyle: 'normal',
      fontHeight: 500,
      lineHeight: '20px',
    }
  },
  select: {
    width: 200,
    height: 36,
    color: theme.palette.common.white,
    borderRadius: 4,
    border: '1px solid rgba(255, 255, 255, 0.20)',
    background: '#000710',
    '& .MuiSvgIcon-root': {
      color: theme.palette.common.white,
    }
  },
  buttonWrapper: {
  },
  button: {
    borderRadius: 3,
    background: '#0042C1',
    color: 'white',
    padding: '8px 16px',
    marginLeft: 8,
    '&:hover': {
      background: '#0042C1',
    },
    '&:disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    }
  },
  downloadButton: {
    border: '1px solid rgba(255, 255, 255, 0.20)',
    borderRadius: 3,
    color: 'white',
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 700,
    margin: '8px 0',
    textTransform: 'none',
    '&:disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    }
  },
})
);

export const formatDate = (date: string) => {

  var d = new Date(date);
  d.setHours(d.getHours() - 5)
  var month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2)
    month = '0' + month;
  if (day.length < 2)
    day = '0' + day;

  return [month, day, year].join('/');
}

interface MenuOption<T> {
  readonly name: string;
  readonly value: string | number;
  readonly option: T;
  readonly lock?: boolean;
  readonly logo?: string;
}

interface OwnProps {
  readonly onEntryFileUpload: (fileUploaded: boolean) => void;
  readonly setUseCustomProjections: () => void;
  readonly useCustomProjections: boolean;
}

interface ReduxProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedType: string;
  readonly selectedSlate: SlateResponse;
  readonly authenticated: APIState<AuthResponse>;
  readonly boostedPlayers: { [key: string]: number };
  readonly poolSize: number;
  readonly loading: boolean;
  readonly stackConstraints: StackConstraint[];
  readonly runback: StackConstraint;
}

interface DispatchProps {
  readonly SetGeneratedSlate: (slate: SlateResponse) => void;
  readonly SetPoolSize: (slate: number) => void;
  readonly SetConstraints: (constraints: StackConstraint[]) => void;
  readonly SetRunback: (runback: StackConstraint) => void;
  readonly TriggerContestGen: (slateId: string, user: string, totalLineups: number, boostedPlayers: { [key: string]: number }, stackConstraints: StackConstraint[], runback: number) => void;
  readonly GetContestPlayers: (slateId: string, user: string) => void;
  readonly GetContestLineups: (slateId: string, user: string) => void;
  readonly GetContestStacks: (slateId: string, user: string) => void;
  readonly SetBoostedPlayer: (player: string, boostedValues: number) => void;
  readonly ClearBoostedPlayers: () => void;
}

type Props = ReduxProps & DispatchProps & OwnProps;

const Settings = (props: Props) => {
  const classes = useStyles();
  const userId = useUserId();
  const [lineupFile, setLineupFile] = React.useState("");
  const [lineupFileName, setLineupFileName] = React.useState("");
  const [lineupFileErrors, setLineupFileErrors] = React.useState([] as string[])
  const [lineupVerified, setLineupVerified] = React.useState(false);
  const [successSnack, setSuccessSnack] = React.useState(false);
  const auth = 'awe' + userId;
  const { selectedSite, selectedSlate, selectedSport, selectedType, authenticated, useCustomProjections, SetGeneratedSlate, onEntryFileUpload } = props;
  const [template, setTemplate] = React.useState<string>('');
  const [projectionsFile] = React.useState("");
  const [hideSettings, setHideSettings] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [disableUpdateInfo, setDisableUpdateInfo] = React.useState(false);
  const { data, isLoading: slatesLoading } = useGetSlates('LATESWAP', false);
  const slates = data?.slates;
  const routeParams = useParams<{ league: string }>();
  const { data: updateInfo } = useGetSlateInfoQuery(selectedSlate?.slateId?.toString(), disableUpdateInfo || selectedSlate?.slateId === undefined)
  const csvInstance = React.useRef<any | null>(null);
  const league = lateSwapSupportedSport(routeParams.league?.toUpperCase() || 'NBA') ? (routeParams.league?.toUpperCase() || 'NBA') : 'NBA';
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);

  const onGenerate = () => {
    if (!hideSettings) {
      SetGeneratedSlate(selectedSlate);
      setHideSettings(true);
      onEntryFileUpload(true);
    } else {
      setHideSettings(false);
      onEntryFileUpload(false);
    }
  }

  React.useEffect(() => {
    if (updateInfo && selectedSlate?.slateId?.toString()) {
      setDisableUpdateInfo(true);
    }
  }, [updateInfo]);

  React.useEffect(() => {
    if (selectedSlate?.slateId?.toString()) {
      setDisableUpdateInfo(false);
    }
  }, [selectedSlate]);

  React.useEffect(() => {
    const validateCsv = async () => {
      setLineupVerified(false);
      if (((!useCustomProjections && lineupFile) || (useCustomProjections && lineupFile && projectionsFile)) && authenticated.status === APIConstants.SUCCEEDED) {
        const request = {
          user: auth,
          slateId: selectedSlate?.slateId,
          customProjections: useCustomProjections ? projectionsFile : '',
          lateSwapEntries: lineupFile,
          tier: authenticated?.data?.authTier,
        };

        await fetch(`${process.env.REACT_APP_API_URL}/api/contests/parsePreContestSim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request)
        })
          .then(res => res.json()).then(res => {
            if (res.errors) {
              setLineupFileErrors([res?.errors?.contestType, res?.errors?.contestData]);
            } else {
              setLineupVerified(true);
              setSuccessSnack(true);
            }
          })
          .catch(err => {
            Sentry.captureException(err);
            console.log('err', err)
          });
      }
    };
    validateCsv().catch(err => {
      Sentry.captureException(err);
      console.log('err', err)
    });
  }, [lineupFile, projectionsFile, useCustomProjections, selectedSite, selectedSlate, selectedSport, authenticated])

  React.useEffect(() => {
    if (template && csvInstance && csvInstance.current && csvInstance.current.link) {
      setTimeout(() => {
        csvInstance.current.link.click();
        setTemplate('');
      });
    }
  }, [template]);

  if (authenticated.status === APIConstants.STARTED) {
    return (
      <SettingsLoader />
    )
  }

  if (!isUserSubscribed) {
    return (
      <UnauthenticatedView title='Late Swap Settings' app='LATESWAP' backgroundColor='rgba(219, 77, 0, 0.20)' />
    )
  }

  if (!slatesLoading && (!slates || (slates && filterSlates(slates, selectedSport, selectedSite, selectedType).length === 0))) {
    return (
      <NoSlatesView title='Late Swap Settings' app='LATESWAP' backgroundColor='rgba(219, 77, 0, 0.20)' />
    )
  }

  return (
    <Grid container className={`${classes.root}`}>
      {
        lineupFileErrors && lineupFileErrors.map(e => (
          <Snackbar
            open={lineupFileErrors.length > 0}
            autoHideDuration={6500}
            onClose={() => setLineupFileErrors([])}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert severity='error'>
              {e}
            </Alert>
          </Snackbar>
        ))
      }
      {
        lineupVerified && (
          <Snackbar
            open={successSnack}
            onClose={() => setSuccessSnack(false)}
            autoHideDuration={4000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            onClick={() => setSuccessSnack(false)}
          >
            <Alert severity='success'>
              File uploaded and verified successfully!
            </Alert>
          </Snackbar>
        )
      }
      <UpgradeModal open={openModal} onClose={() => setOpenModal(false)} />
      {/* {
        !hideSettings && (
          <Grid container item alignItems='center' justifyContent='space-between' xs={12}>
            <Stack direction='row' alignItems='center'>
              <Box className={classes.heading}>Late Swap Settings</Box>
            </Stack>
            <Stack>
              {updateInfo?.projectionsLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Projections Last Updated: {moment.utc(updateInfo?.projectionsLastUpdated).local().format('M/D h:mm A')}</Box>}
              {updateInfo?.ownershipLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Ownership Last Updated: {moment.utc(updateInfo?.ownershipLastUpdated).local().format('M/D h:mm A')}</Box>}
            </Stack>
          </Grid>
        )
      } */}
      <Grid container >
        <Grid item xs={12} container className={`${classes.heading}`} justifyContent='space-between'>
          <Stack direction='row' flexWrap='wrap' alignItems="center">
            {
              !hideSettings ? (
                <Stack direction='row' alignItems='flex-end'>
                  <SettingsSelectors app='LATESWAP' />
                  {slates && slates.length > 0 && (
                    <Box marginTop={selectedSlate.sport?.toLowerCase() === 'mlb' ? -1.5 : 1} display='flex' alignItems='flex-end'>
                      <Upload
                        inputId="lineupsInput"
                        onUpload={(lineups: string) => setLineupFile(lineups)}
                        onUploadSetFileName={(fileName: string) => setLineupFileName(fileName)}
                        selectedSlate={selectedSlate}
                        label="Upload Entry File CSV"
                        defaultFileName={lineupFileName}
                        hideDropzone
                        captionUnderneath
                      />
                    </Box>
                  )}
                </Stack>
              ) : (
                <>
                  <Stack direction='column'>
                    <Box fontSize="12px" marginBottom="8px" style={{ opacity: 0.7 }}>League</Box>
                    <Stack direction="row">
                      {league}
                      <Box height="24px" marginLeft="4px">
                        <img src={sportLogos[league].logo} alt={sportLogos[league].name} style={{ width: '24px', height: '100%', objectFit: 'contain' }} />
                      </Box>
                    </Stack>
                  </Stack>
                  <Stack direction='column'>
                    <Box fontSize="12px" marginBottom="8px" style={{ opacity: 0.7 }}>Site</Box>
                    <Stack direction="row">
                      {selectedSite.site}
                      <Box height="24px" marginLeft="4px">
                        <img src={siteLogos[selectedSite.site?.toLowerCase()].logo} alt={siteLogos[selectedSite.site?.toLowerCase()].logo} style={{ width: '24px', height: '100%', objectFit: 'contain' }} />
                      </Box>
                    </Stack>
                  </Stack>
                  <Stack direction='column'>
                    <Box fontSize="12px" marginBottom="8px" style={{ opacity: 0.7 }}>Slate</Box>
                    <Stack direction="row">
                      {selectedSlate.name}
                      <Stack fontSize="14px" marginLeft="4px" direction="row" alignItems="center">
                        ({new Date(selectedSlate.startTime).toLocaleDateString([], { month: 'short', day: '2-digit', timeStyle: undefined })} - {new Date(selectedSlate.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} EST)
                      </Stack>
                    </Stack>
                  </Stack>
                </>
              )
            }
          </Stack>
          <Stack direction='row' alignItems='center' className={classes.buttonWrapper}>
            <Stack>
              {updateInfo?.projectionsLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Projections Last Updated: {moment.utc(updateInfo?.projectionsLastUpdated).local().format('M/D h:mm A')}</Box>}
              {updateInfo?.ownershipLastUpdated && <Box fontSize={10} color={'#BCBCBC'} paddingRight={2} textAlign={'end'}>Ownership Last Updated: {moment.utc(updateInfo?.ownershipLastUpdated).local().format('M/D h:mm A')}</Box>}
            </Stack>
            <Button variant={hideSettings ? 'outlined' : 'contained'} size='small' className={`${hideSettings ? classes.restart : classes.button}`} onClick={() => onGenerate()} disabled={!lineupVerified}>
              {hideSettings ? 'Restart' : 'Continue'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  )
}


const mapStateToProps = (state: any) => {
  const {
    boostedPlayers,
    poolSize,
    loading,
    stackConstraints,
    runback,
    contestLineups,
  } = state.lateSwap;

  const {
    authenticated,
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate
  } = state.common;

  return {
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate,
    contestLineups,
    authenticated,
    boostedPlayers,
    poolSize,
    loading,
    stackConstraints,
    runback,
  }
}


const mapDispatchToProps = {
  SetGeneratedSlate: setGeneratedSlate,
  TriggerContestGen: triggerContestGen,
  SetPoolSize: setPoolSize,
  SetConstraints: setConstraints,
  SetRunback: setRunback,
  GetContestPlayers: getContestPlayers,
  GetContestLineups: getContestLineups,
  GetContestStacks: getContestStacks,
  SetBoostedPlayer: setBoostedPlayer,
  ClearBoostedPlayers: clearBoostedPlayer,
}

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
